/**
 * Schedule View — Vuex module
 *
 * Backs the daily operatory schedule page (`/app/schedule`): the day's appointments laid out by
 * operatory the way Open Dental draws them, plus (from Phase 3) the per-appointment alert flags.
 *
 * Plan: docs/plans/2026-09-19-schedule-view-plan.md
 *
 * ⛔ NO /runquery. Schedule View is new code, so it never hand-interpolates SQL from the browser
 * the way the legacy call sites do. Everything goes through schedule-view-service.
 *
 * WHY `date` STARTS NULL rather than "today": the office's day is the day in the SITE's timezone
 * (GENERAL_TIMEZONE), not the browser's. A default computed here would be wrong for any user
 * sitting in a different timezone from the practice — which is our own everyday case, and every
 * multi-clinic group's. `todayForSite()` resolves it properly and the page calls that on mount.
 */

import axios from 'axios';
import moment from 'moment-timezone';
import { extractApiError } from '@/utils/apiError';
import { emptyFilters, normalizeFilters, readFilters, writeFilters } from '@/utils/scheduleView/filters';
import {
  DEFAULT_TABLE_PREFS, normalizeTablePrefs, readTablePrefs, writeTablePrefs,
} from '@/utils/scheduleView/tableView';
import { buildSettingsPayload, ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import { collection, doc, query, where } from 'firebase/firestore';
import { safeSnapshot } from '@/store/firestoreListen';
import {
  shouldRefetch, signalChangedDate, readSignalCounters,
  readAlertCounters, changedAlertKeys, ALERT_SIGNAL_DEBOUNCE_MS,
  nextChaseStep, noteChaseRedraw, SIGNAL_CHASE_INTERVAL_MS,
} from '@/utils/scheduleView/liveness';
import { chunkPatNums, mergeTaskAlerts, mergeAlertPayload, TASK_CHUNK_SIZE } from '@/utils/scheduleView/alerts';
import { accessRefusalFrom } from '@/utils/scheduleView/access';
import { checkKey } from '@/utils/scheduleView/checklist';
import { taskQueryValues, foldTaskSnapshot, combineTaskChunks, nameTaskLines } from '@/utils/scheduleView/taskLines';
import { stripMentions } from '@/utils/taskPatients';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

/** Latest-wins guard: a slower earlier request must not overwrite a newer day. */
let dayFetchSeq = 0;
/** The same guard for alerts, counted separately — the two endpoints race independently. */
let alertFetchSeq = 0;
/** And for the pulse, which runs on the shortest clock of the three. */
let pulseFetchSeq = 0;
/**
 * The live-signal listener's unsubscribe (Phase 3Lb), or null.
 *
 * Module-level rather than in state because Vuex state must stay serialisable — a function in the
 * store breaks devtools, and any state snapshot taken for debugging.
 */
let signalUnsubscribe = null;

/**
 * Live alert refresh (Phase 3, 2026-09-23) — a cloud bump of `alerts.<key>` on the signal document
 * re-asks `/alerts` for just that key (see refreshAlertKeys).
 *
 * LIVE_ALERT_KEYS is the whitelist the signal reader applies: every key the server can answer for.
 * `task` is excluded — it is Firestore-only, so `/alerts` has nothing to re-run for it.
 * The rest is bookkeeping, module-level for the same serialisability reason as signalUnsubscribe:
 *   • pendingAlertKeys + alertRefreshTimer: the debounce that turns a burst into ONE request;
 *   • targetedAlertSeq + latestTargetedSeqByKey: latest-wins PER KEY, so an older answer for
 *     `forms` can never overwrite a newer one — without making a `txsign` answer cancel a `forms` one.
 */
const LIVE_ALERT_KEYS = ALERT_KEYS.filter(key => key !== 'task');
let pendingAlertKeys = [];
let alertRefreshTimer = null;
let targetedAlertSeq = 0;
let latestTargetedSeqByKey = {};

/**
 * The signal chase (2026-09-25) — see chaseSignal. Module-level for the same serialisability
 * reason as signalUnsubscribe.
 *   • chaseTimer: the ONE pending retry for the page. A new bump cancels it before anything else.
 *   • chaseState: `{ run, tries, startDate, bumps, moves, lastSeenVersion }` for the live chase,
 *     or null. `bumps` / `moves` are nudges received vs redraws seen (see nextChaseStep).
 *   • chaseRun: which chase is current. A tick that was already in flight when a newer bump (or
 *     unwatchSignal) replaced its chase must not schedule anything when it lands — that is how a
 *     page would end up running two loops.
 */
let chaseTimer = null;
let chaseState = null;
let chaseRun = 0;

/**
 * The `task` alert's listeners (Phase 4), one per chunk of 30 patients, and their latest results.
 *
 * ⛔ Why this alert alone is live rather than server-computed: the case is "tag a patient in a task
 * and the icon turns red immediately". A request/response endpoint answers with whatever was true
 * when the page last asked, which on a 30-second poll means up to half a minute of a task the
 * front desk has just created not existing on screen.
 *
 * Results are kept PER CHUNK because each listener fires independently: a snapshot from chunk 2
 * knows nothing about chunk 1's patients, so committing one chunk's map on its own would wipe the
 * others' icons every time anybody edited a task.
 *
 * Module-level rather than in state for the same reason as `signalUnsubscribe` — Vuex state must
 * stay serialisable.
 */
/**
 * The visit checklist's shared ticks (Phase 10c).
 *   • checksFetchSeq: a newer /checks request wins; an older answer is dropped.
 *   • checksRefreshTimer: the debounce that turns a burst of `alerts.checks` bumps into ONE fetch.
 *   • pendingToggles: ticks this browser has drawn but the server has not confirmed yet, keyed by
 *     checkKey → { checked, token }. ⛔ A /checks answer is laid UNDER these: somebody ticking A
 *     then B fires a refetch (A's own nudge) that can answer before B is saved — without this, B
 *     would blink off and back on.
 */
let checksFetchSeq = 0;
let checksRefreshTimer = null;
let pendingToggles = {};
let toggleToken = 0;

/** The signal-document counter main-service bumps after a tick (schedule-view-check.js). */
const CHECKS_SIGNAL_KEY = 'checks';

let taskUnsubscribes = [];
let taskChunkResults = [];

/**
 * Today's date (YYYY-MM-DD) in the practice's timezone.
 *
 * Exported so the page and its tests use the SAME rule. An unknown or unset timezone falls back to
 * the browser's own day rather than guessing a timezone: being an hour out at the edges of the day
 * is recoverable, silently showing a different practice's idea of "today" is not.
 */
export function todayForSite(timezone) {
  if (timezone && moment.tz.zone(timezone)) return moment.tz(timezone).format('YYYY-MM-DD');
  return moment().format('YYYY-MM-DD');
}

/** Minutes past midnight, right now, in the practice's timezone. Drives the NOW line. */
export function nowMinutesForSite(timezone) {
  const m = (timezone && moment.tz.zone(timezone)) ? moment.tz(timezone) : moment();
  return m.hours() * 60 + m.minutes();
}

/**
 * A task's due DAY as 'MM/DD/YYYY', or '' when it has none.
 *
 * ⛔ Formatted in the SITE's timezone, not the browser's. `dueAt` is saved as the office's
 * start-of-day, so a viewer sitting in an earlier timezone than the practice would read
 * "midnight the day before" and the checklist would print yesterday's date (and call a task due
 * today "late"). Same reasoning as `todayForSite`; an unknown timezone falls back to the browser's.
 *
 * Reads `dueAt` only. `dueAtStr` is NOT used: it turns up in three different formats in real data.
 */
function taskDueDay(data, timezone) {
  const due = data && data.dueAt;
  if (!due || typeof due.toDate !== 'function') return '';
  const date = due.toDate();
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return (timezone && moment.tz.zone(timezone))
    ? moment.tz(date, timezone).format('MM/DD/YYYY')
    : moment(date).format('MM/DD/YYYY');
}

/** { '<EmployeeID>': 'Name' } from the customer module's employee list (empty when it has not loaded). */
function employeeNameMap(employees) {
  const out = {};
  (Array.isArray(employees) ? employees : []).forEach((e) => {
    if (e && e.EmployeeID !== undefined && e.EmployeeID !== null && e.Name) out[String(e.EmployeeID)] = e.Name;
  });
  return out;
}

/**
 * localStorage, or null where there isn't one.
 *
 * Reading `window.localStorage` THROWS (not returns null) in Safari private browsing and wherever
 * site data is blocked, so every access goes through here and the filter helpers swallow the rest.
 * A remembered filter is a convenience; losing it must never cost the page.
 */
function browserStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

export default {
  namespaced: true,

  state: {
    /** YYYY-MM-DD in the SITE's timezone. Null until the page resolves it (see note above). */
    date: null,
    /** The `/schedule-view/day` payload: operatories, providers, appointments, blockouts, settings. */
    day: null,
    /**
     * The `/schedule-view/alerts` payload: `{ date, byPatNum, ran, errors }`.
     *
     * Null until the first load. NOT reset to null on a refresh — the icons stay on screen while
     * the next answer is in flight, because a row of icons blinking out every two minutes reads as
     * "the alerts just cleared", which is the opposite of what happened.
     */
    alerts: null,
    /** True while an alert fetch is in flight. Only used to avoid stacking requests. */
    alertsLoading: false,
    /**
     * Live open-task counts per PatNum, from Firestore: `{ '<PatNum>': <count> }`.
     *
     * ⛔ COUNTS ONLY. This is the ONLY task state that reaches `alertsForDate`, and from there the
     * block icon tooltip and the Alerts dropdown counts, which stay counts: Firestore bypasses the PHI demo
     * mask and a task routinely contains a patient's name — see `describeAlert('task')`. Task text
     * lives in `taskLines` below and nowhere else.
     *
     * Null until the first snapshot, which is different from `{}`: null means "we are not
     * watching, or have not heard yet", `{}` means "watched, and nobody has an open task". Only
     * the second is safe to draw as "no icons".
     */
    taskCounts: null,
    /**
     * The open tasks themselves, per PatNum, from the SAME listener as `taskCounts`:
     * `{ '<PatNum>': [{ id, text, dueAt, who, assignees }] }` — soonest due first, `assignees` being
     * the EmployeeIDs `who` was made from. Same null-vs-`{}` meaning. Read it through the
     * `taskLinesByPatNum` getter, which re-resolves `who` against the roster as it is now.
     *
     * ⛔ Task text is allowed in ONE place, here, and for the visit checklist only. The PHI demo
     * mask does not reach it: Firestore bypasses the axios boundary where masking happens (the
     * same documented limit as `src/components/PatientPanel/tabs/TasksTab.vue`), and task text
     * often names a patient. Recordings use a synthetic site. The block tooltip and the Alerts
     * dropdown stay counts — never feed these lines to `mergeTaskAlerts` or the alerts payload.
     */
    taskLines: null,
    loading: false,
    /** User-facing message, already run through extractApiError. Null when there is no error. */
    error: null,
    /** Set when the site has no Open Dental mirror yet — a known state, not a failure. */
    unavailable: null,
    /**
     * The service's refusal of this OFFICE — { unavailable: 'NOT_ENABLED' | 'EXE_TOO_OLD', exeVersion,
     * minExeVersion } — set when ANY schedule-view call answers 403 with an `unavailable` body
     * (utils/scheduleView/access.js accessRefusalFrom). A known state, never an error: the page shows a
     * plain empty state (a Super Admin also gets a banner naming the reason). Null while the office has
     * access; cleared by the next call that succeeds.
     */
    accessRefusal: null,
    /**
     * True once the live-signal listener (watchSignal) has delivered a snapshot and has not failed since.
     * Half of "the live signal is healthy" (liveness.js isSignalHealthy — the other half is the
     * freshness indicator's `sync`); it decides how often the page asks `/pulse`.
     */
    signalListening: false,
    /**
     * Provider / Operatory / Status narrowing. PER BROWSER, not per office — one person's view of
     * the day is not a practice-wide decision. Empty lists mean "show everything".
     */
    filters: emptyFilters(),
    /**
     * How the page reads the day (Phase 11): 'schedule' (the grid) or 'table' on an iPad / desktop,
     * and how the table orders its rows: 'time' or 'op'. PER BROWSER and per site, exactly like
     * `filters` — one person's way of reading the day, never a site setting and never in the settings
     * blob. Kept under its OWN localStorage key (tableView.js), not inside the filters, so "Clear
     * filters" cannot flip the view back.
     *
     * `phoneView` (WP-B, 2026-10-23) is the PHONE's own choice — 'timeline' | 'columns' | 'table' —
     * in the same blob: a phone and an iPad keep separate choices, so neither rewrites the other's.
     */
    view: DEFAULT_TABLE_PREFS.view,
    phoneView: DEFAULT_TABLE_PREFS.phoneView,
    tableSort: DEFAULT_TABLE_PREFS.sort,
    /** True while a settings save is in flight. */
    savingSettings: false,

    /**
     * The last `/schedule-view/pulse` answer: `{ date, v, n, sync }`.
     *
     * `sync` is what lets the page admit it has gone stale. Kept separately from `day` because the
     * two have very different lifetimes — the pulse is replaced every 30 seconds, the day only
     * when something actually changed.
     */
    pulse: null,
    /**
     * The version of the day currently DRAWN on screen.
     *
     * Not the same as `pulse.v`: this is the version as of the last successful `/day`, and the
     * gap between the two is precisely "the screen is out of date". Null after a date change, so
     * the first pulse on a new day adopts its version instead of treating it as a change and
     * refetching a day that was just loaded.
     */
    drawnVersion: null,
    /**
     * Single-flight guard for the live loop. On a slow connection or a big practice a `/day`
     * refetch can outlast the poll interval; without this the timer would stack requests on top of
     * each other, and the practice that is already struggling gets the most load.
     */
    liveBusy: false,
    /**
     * The live signal's per-date counters, as last seen (Phase 3Lb). Null until the first
     * snapshot — which is the baseline, not a change. Dates and numbers only; no patient data
     * ever enters this, by construction on both ends.
     */
    signalCounters: null,
    /**
     * The signal's per-ALERT counters, as last seen (Phase 3 live refresh). Null until the first
     * snapshot, which is the baseline. Alert keys and numbers only — whitelisted on the way in.
     */
    alertCounters: null,
    /**
     * The visit checklist's shared ticks for `checksDate` (Phase 10c), keyed
     * "<AptNum>:<ItemKey>" (checklist.js checkKey) → { by: EmployeeID|null, at: UTC|null }.
     * Present = ticked. Ticks are office-wide, stored in teamio.schedule_view_checks.
     */
    checks: {},
    checksDate: null,
    /** 'NO_CHECKS_TABLE' | 'NO_OD_DATA' when the server answered empty for a known reason. */
    checksUnavailable: null,
    /** The signal's `alerts.checks` counter as last seen. Null until the first snapshot (baseline). */
    checksCounter: null,
  },

  mutations: {
    setDate(state, date) {
      state.date = date;
    },
    setDay(state, day) {
      state.day = day;
    },
    setAlerts(state, alerts) {
      state.alerts = alerts;
    },
    setTaskCounts(state, counts) {
      state.taskCounts = counts;
    },
    setTaskLines(state, lines) {
      state.taskLines = lines;
    },
    setAlertsLoading(state, loading) {
      state.alertsLoading = !!loading;
    },
    setLoading(state, loading) {
      state.loading = !!loading;
    },
    setError(state, error) {
      state.error = error || null;
    },
    setUnavailable(state, reason) {
      state.unavailable = reason || null;
    },
    setAccessRefusal(state, refusal) {
      state.accessRefusal = refusal || null;
    },
    setSignalListening(state, listening) {
      state.signalListening = !!listening;
    },
    setFilters(state, filters) {
      state.filters = normalizeFilters(filters);
    },
    /** Anything but a known view is the default view — a corrupt value must not blank the page. */
    setView(state, view) {
      state.view = normalizeTablePrefs({ view }).view;
    },
    /** Same rule for the phone's three views: anything unknown is the default (the timeline). */
    setPhoneView(state, phoneView) {
      state.phoneView = normalizeTablePrefs({ phoneView }).phoneView;
    },
    setTableSort(state, sort) {
      state.tableSort = normalizeTablePrefs({ sort }).sort;
    },
    setSavingSettings(state, saving) {
      state.savingSettings = !!saving;
    },
    setPulse(state, pulse) {
      state.pulse = pulse || null;
    },
    setDrawnVersion(state, version) {
      state.drawnVersion = version || null;
    },
    setLiveBusy(state, busy) {
      state.liveBusy = !!busy;
    },
    setSignalCounters(state, counters) {
      state.signalCounters = counters || null;
    },
    setAlertCounters(state, counters) {
      state.alertCounters = counters || null;
    },
    setChecks(state, { date, checks, unavailable } = {}) {
      state.checksDate = date || null;
      state.checks = checks || {};
      state.checksUnavailable = unavailable || null;
    },
    /** One tick on or off. A NEW object every time — Vue 2 does not see a key added or deleted in place. */
    setCheck(state, { key, value }) {
      const next = Object.assign({}, state.checks);
      if (value) next[key] = value;
      else delete next[key];
      state.checks = next;
    },
    setChecksCounter(state, counter) {
      state.checksCounter = counter || null;
    },
  },

  getters: {
    /** True once the day's schedule has loaded and can be drawn. */
    hasDay: state => !!state.day,
    /** Resolved settings, or null before the first load. The page never keeps its own defaults. */
    settings: state => (state.day ? state.day.settings : null),
    timezone: state => (state.day ? state.day.timezone : null),
    /**
     * Which gated alerts this site can compute at all. Nothing claimed until the server says so:
     * before the first load every gated alert reads unavailable, which greys its checkbox rather
     * than offering one that quietly does nothing.
     */
    capabilities: state => (
      (state.day && state.day.capabilities)
        ? state.day.capabilities
        : { forms: false, medhx: false, txsign: false, insverify: false }
    ),
    /**
     * WHY each gated alert is unavailable, from the server: { <alertKey>: 'NEEDS_FORMS' | ... }
     * (settingsForm.js CAPABILITY_REASON_TEXT). Null from a service that predates the reasons — the
     * popover then falls back to its own hint text.
     */
    capabilityReasons: state => (
      (state.day && state.day.capabilityReasons && typeof state.day.capabilityReasons === 'object')
        ? state.day.capabilityReasons
        : null
    ),

    /**
     * The alerts, but ONLY if they belong to the day currently on screen.
     *
     * `fetchAlerts` deliberately keeps the previous payload through a failure so icons do not blink
     * out on a network blip. That is right for a refresh and WRONG across a date change — it would
     * decorate Tuesday's grid with Monday's overdue balances, which is worse than showing nothing.
     * The payload echoes its own date, so matching on that covers both cases without any careful
     * clearing at the call sites.
     */
    alertsForDate: (state) => {
      const payload = (state.alerts && state.alerts.date === state.date) ? state.alerts : null;
      // ⛔ The live `task` counts are folded in HERE, in one place, so blocks, tooltips and the
      // Alerts dropdown all see one payload shape and none of them needs a special case for the one
      // alert that does not come from the server.
      //
      // Merged onto the server payload rather than standing alone: until `/alerts` answers there
      // is no `ran` list, and an alert absent from `ran` draws nothing anyway. `/alerts` lands a
      // second or two after the day, so the window where a task icon is not yet drawn is the same
      // window in which no icons are drawn at all.
      return mergeTaskAlerts(payload, state.taskCounts);
    },

    /**
     * The day's open tasks per PatNum, for the visit checklist ONLY (Phase 10d) — `{}` until the
     * listener has answered. Deliberately a separate getter from `alertsForDate`: task text must
     * never ride the alerts payload into the tooltip or the Alerts dropdown.
     *
     * ⛔ The assignees' names are resolved HERE, against `customer.employees` as it is now, and not
     * only when the snapshot is folded. The employee list arrives in the slow startup batch (with
     * the patient roster) while this page's day answers in about a second, so on a hard refresh the
     * listener can take its first snapshot before any employee exists. Names frozen at that moment
     * would print "Unassigned" for an assigned task until that chunk's listener happened to fire
     * again — a false statement in front of the front desk. Reading rootState makes the getter
     * recompute the moment the roster lands, with no new snapshot.
     */
    taskLinesByPatNum: (state, getters, rootState) => nameTaskLines(
      state.taskLines || {},
      employeeNameMap(rootState.customer && rootState.customer.employees)
    ),

    /**
     * Whether the Open Dental mirror is still moving, for the freshness indicator.
     *
     * Nothing claimed before the first pulse lands: `unknown`, not `live`. A page that assumed
     * health until told otherwise would spend its first seconds — and its whole life, if the pulse
     * never succeeds — confidently telling the office it was live, which is the one thing this
     * indicator exists to stop it doing.
     *
     * Matched on date for the same reason as `alertsForDate`: a pulse answering for a day that is
     * no longer on screen says nothing useful about the day that is.
     */
    sync: state => (
      (state.pulse && state.pulse.date === state.date && state.pulse.sync)
        ? state.pulse.sync
        : { state: 'unknown', syncedAt: null, ageSeconds: null }
    ),
  },

  actions: {
    /**
     * Load one day.
     *
     * `silent` is for the background refresh: re-showing the skeleton every two minutes makes the
     * page flicker under someone who is reading it, so a refresh keeps the current grid on screen
     * and swaps it only when the new data arrives.
     */
    async fetchDay({ commit, state, rootState }, { date, silent = false } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      if (!siteID) {
        console.warn('[scheduleView] fetchDay called before the site was known');
        return null;
      }
      if (!date) {
        console.warn('[scheduleView] fetchDay called without a date');
        return null;
      }

      const seq = ++dayFetchSeq;
      // A different day is a different thing to watch. Carrying the old version over would make
      // the first pulse on the new day look like a change and refetch a day just loaded; carrying
      // the old pulse over would let Tuesday's freshness describe Monday's screen.
      if (date !== state.date) {
        commit('setDrawnVersion', null);
        commit('setPulse', null);
      }
      commit('setDate', date);
      if (!silent) commit('setLoading', true);
      commit('setError', null);

      try {
        const response = await axios.post('/schedule-view/day', { siteID, date }, { crossdomain: true });

        if (seq !== dayFetchSeq) return null; // superseded while in flight

        if (response.data && response.data.success) {
          const payload = response.data.data;
          commit('setAccessRefusal', null);
          commit('setDay', payload);
          commit('setUnavailable', payload.unavailable || null);
          return payload;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load the schedule');
      } catch (err) {
        if (seq !== dayFetchSeq) return null;
        // The office has no access (switch off, app too old — a stale tab, or a Super Admin on such an
        // office): a known state with its own empty state, NOT an error line or a toast. The drawn day
        // goes too — a screen that says "unavailable" must not keep showing yesterday's patients.
        const refusal = accessRefusalFrom(err);
        if (refusal) {
          commit('setAccessRefusal', refusal);
          commit('setDay', null);
          return null;
        }
        console.error('[scheduleView] fetchDay error:', err);
        commit('setError', extractApiError(err, 'Failed to load the schedule. Please try again.'));
        return null;
      } finally {
        if (seq === dayFetchSeq && !silent) commit('setLoading', false);
      }
    },

    /**
     * Load the day's alert flags.
     *
     * Deliberately NOT awaited by the page's day load: the grid paints as soon as the Open Dental
     * mirror answers and the icons arrive when they arrive. A slow claims query on a big practice
     * must never be the reason nobody can see their schedule.
     *
     * A failure leaves the PREVIOUS alerts on screen rather than clearing them. Icons vanishing on
     * a network blip reads as "everything just got resolved", which is the most misleading thing
     * this payload could do; stale-but-labelled beats confidently-empty.
     */
    async fetchAlerts({ commit, dispatch, state, rootState }, { date } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const day = date || state.date;
      if (!siteID || !day) return null;

      const seq = ++alertFetchSeq;
      commit('setAlertsLoading', true);
      try {
        const response = await axios.post('/schedule-view/alerts', { siteID, date: day }, { crossdomain: true });
        if (seq !== alertFetchSeq) return null; // superseded while in flight

        if (response.data && response.data.success) {
          commit('setAlerts', response.data.data);
          // The ticks ride the alerts' clock too, so a browser whose Firestore is blocked (no live
          // nudge) still catches up with everyone else's ticks. Not awaited: icons first.
          dispatch('fetchChecks', { date: day });
          return response.data.data;
        }
        throw new Error((response.data && response.data.message) || 'Failed to load alerts');
      } catch (err) {
        if (seq !== alertFetchSeq) return null;
        const refusal = accessRefusalFrom(err);
        if (refusal) { commit('setAccessRefusal', refusal); return null; }
        // Logged, not surfaced as a page error: the schedule itself is fine and still readable.
        // The page reports which rules are missing from the payload's own `errors` list.
        console.error('[scheduleView] fetchAlerts error:', err);
        return null;
      } finally {
        if (seq === alertFetchSeq) commit('setAlertsLoading', false);
      }
    },

    /**
     * Re-ask `/alerts` for ONLY these keys and fold the answer into what is on screen (Phase 3 live
     * refresh). Driven by the cloud signal — a form coming back bumps `alerts.forms`.
     *
     * Quiet on purpose: no loading flag and no error banner. The full payload is already drawn; this
     * only makes one rule's icons catch up sooner than the ten-minute clock would.
     *
     * ⛔ Three ways an answer is DROPPED rather than applied, each because applying it would be
     * wrong rather than merely late:
     *   • a full fetch started after this one — it answers for these keys too, and is newer;
     *   • the page moved to another date — the answer is about a day no longer on screen;
     *   • a newer targeted request for the same key is in flight — latest wins, per key.
     * And if there is no full payload for the date to fold into, it asks for EVERYTHING instead:
     * installing a keyed answer on its own would blank every other icon (see mergeAlertPayload).
     */
    async refreshAlertKeys({ commit, dispatch, state, rootState }, { date, keys } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const day = date || state.date;
      const wanted = (keys || []).filter(key => LIVE_ALERT_KEYS.indexOf(key) !== -1);
      if (!siteID || !day || !wanted.length) return null;

      const seq = ++targetedAlertSeq;
      wanted.forEach((key) => { latestTargetedSeqByKey[key] = seq; });
      const fullSeqAtStart = alertFetchSeq;
      try {
        const response = await axios.post('/schedule-view/alerts', { siteID, date: day, keys: wanted }, { crossdomain: true });
        if (!(response.data && response.data.success)) {
          throw new Error((response.data && response.data.message) || 'Failed to refresh alerts');
        }
        if (alertFetchSeq !== fullSeqAtStart) return null;
        if (state.date !== day) return null;
        const current = wanted.filter(key => latestTargetedSeqByKey[key] === seq);
        if (!current.length) return null;

        const merged = mergeAlertPayload(state.alerts, response.data.data, current);
        if (merged) {
          commit('setAlerts', merged);
          return merged;
        }
        return dispatch('fetchAlerts', { date: day });
      } catch (err) {
        const refusal = accessRefusalFrom(err);
        if (refusal) { commit('setAccessRefusal', refusal); return null; }
        // Logged only. The icons on screen stay as they were, and the ten-minute clock still runs.
        console.error('[scheduleView] refreshAlertKeys error:', err);
        return null;
      }
    },

    /**
     * Load the day's shared checklist ticks (Phase 10c) — schedule-view-service /schedule-view/checks.
     *
     * Quiet, like the alerts: a failure keeps the ticks on screen rather than clearing them (ticks
     * vanishing on a network blip would read as "somebody unticked everything").
     */
    async fetchChecks({ commit, state, rootState }, { date } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const day = date || state.date;
      if (!siteID || !day) return null;

      const seq = ++checksFetchSeq;
      try {
        const response = await axios.post('/schedule-view/checks', { siteID, date: day }, { crossdomain: true });
        if (seq !== checksFetchSeq || state.date !== day) return null; // superseded, or the page moved on
        if (!(response.data && response.data.success)) {
          throw new Error((response.data && response.data.message) || 'Failed to load checklist');
        }
        const data = response.data.data || {};
        const checks = {};
        (data.checks || []).forEach((row) => {
          checks[checkKey(row.AptNum, row.ItemKey)] = { by: row.CheckedBy || null, at: row.CheckedAt || null };
        });
        // ⛔ What this browser has drawn and not yet had confirmed wins over the server's answer.
        const me = (rootState.customer && rootState.customer.userID) || null;
        Object.keys(pendingToggles).forEach((key) => {
          if (pendingToggles[key].checked) checks[key] = checks[key] || { by: me, at: null };
          else delete checks[key];
        });
        commit('setChecks', { date: day, checks, unavailable: data.unavailable || null });
        return checks;
      } catch (err) {
        const refusal = accessRefusalFrom(err);
        if (refusal) { commit('setAccessRefusal', refusal); return null; }
        console.error('[scheduleView] fetchChecks error:', err);
        return null;
      }
    },

    /**
     * Tick or untick one checklist item. OPTIMISTIC: drawn at once, saved through main-service
     * /schedule-view-check, rolled back if the save fails.
     *
     * Returns { ok: true } or { ok: false, error } — the caller shows the toast, so the store
     * stays free of UI.
     */
    async toggleCheck({ commit, state, rootState }, { aptNum, itemKey, checked } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      if (!siteID || !aptNum || !itemKey) return { ok: false, error: 'Missing appointment or item' };
      const key = checkKey(aptNum, itemKey);
      const previous = state.checks[key] || null;
      const employeeID = (rootState.customer && rootState.customer.userID) || null;
      const token = ++toggleToken;

      pendingToggles[key] = { checked: !!checked, token };
      commit('setCheck', { key, value: checked ? (previous || { by: employeeID, at: null }) : null });

      try {
        const response = await axios.post('/schedule-view-check',
          { siteID, aptNum, itemKey, checked: !!checked, employeeID }, { crossdomain: true });
        if (!(response.data && response.data.success)) {
          throw new Error((response.data && response.data.message) || 'Could not save the checkmark');
        }
        if (pendingToggles[key] && pendingToggles[key].token === token) delete pendingToggles[key];
        return { ok: true };
      } catch (err) {
        // Roll back ONLY if nobody toggled this item again meanwhile — a later click owns it now.
        if (pendingToggles[key] && pendingToggles[key].token === token) {
          delete pendingToggles[key];
          commit('setCheck', { key, value: previous });
        }
        console.error('[scheduleView] toggleCheck error:', err);
        return { ok: false, error: extractApiError(err, 'Could not save the checkmark') };
      }
    },

    /** Debounce `alerts.checks` bumps into one /checks fetch (same window as the alert refresh). */
    queueChecksRefresh({ dispatch, state }) {
      if (checksRefreshTimer) clearTimeout(checksRefreshTimer);
      checksRefreshTimer = setTimeout(() => {
        checksRefreshTimer = null;
        dispatch('fetchChecks', { date: state.date });
      }, ALERT_SIGNAL_DEBOUNCE_MS);
    },

    /**
     * Collect alert keys from signal bumps and ask once they stop arriving (trailing debounce).
     * A three-section packet is three bumps in a few seconds; this makes it one request.
     */
    queueAlertRefresh({ dispatch, state }, keys) {
      (keys || []).forEach((key) => {
        if (pendingAlertKeys.indexOf(key) === -1) pendingAlertKeys.push(key);
      });
      if (!pendingAlertKeys.length) return;
      if (alertRefreshTimer) clearTimeout(alertRefreshTimer);
      alertRefreshTimer = setTimeout(() => {
        const due = pendingAlertKeys;
        pendingAlertKeys = [];
        alertRefreshTimer = null;
        dispatch('refreshAlertKeys', { date: state.date, keys: due });
      }, ALERT_SIGNAL_DEBOUNCE_MS);
    },

    /**
     * Ask the cheap question: has this day changed, and is the mirror still live?
     *
     * Three indexed aggregates and no PHI — this is what makes a schedule that follows Open Dental
     * affordable. Running `/day` on the same clock would be six joined queries per tab per tick to
     * discover, almost every time, that nothing moved.
     *
     * A failure is logged and swallowed. The pulse is a background nicety; a page error over a
     * perfectly good schedule because one poll timed out would be a worse page than one that
     * quietly retries in thirty seconds.
     */
    async fetchPulse({ commit, state, rootState }, { date } = {}) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const day = date || state.date;
      if (!siteID || !day) return null;

      const seq = ++pulseFetchSeq;
      try {
        const response = await axios.post('/schedule-view/pulse', { siteID, date: day }, { crossdomain: true });
        if (seq !== pulseFetchSeq) return null; // superseded while in flight

        if (response.data && response.data.success) {
          commit('setPulse', response.data.data);
          return response.data.data;
        }
        return null;
      } catch (err) {
        if (seq !== pulseFetchSeq) return null;
        const refusal = accessRefusalFrom(err);
        if (refusal) { commit('setAccessRefusal', refusal); return null; }
        console.error('[scheduleView] fetchPulse error:', err);
        return null;
      }
    },

    /**
     * One tick of the live loop: pulse, and refetch the day only if it actually moved.
     *
     * ── WHY THIS LIVES IN THE STORE ───────────────────────────────────────────────────────────
     * It is the single refetch path. Every source that might want to say "something changed" — the
     * timer, the tab regaining focus, and (Phase 3Lb) the exe's signal — calls THIS, so there is
     * one place where single-flighting, date-matching and the alert rule are decided. Three
     * callers each doing their own version of it is how a page ends up refetching four times for
     * one change, or not at all for another.
     *
     * ── SINGLE-FLIGHT ────────────────────────────────────────────────────────────────────────
     * On a big practice or a poor connection a `/day` refetch can outlast the poll interval. The
     * guard means ticks are DROPPED while one is in flight rather than queued: a dropped tick
     * costs at most one interval of latency, whereas a queue would pile requests onto the practice
     * that is already struggling and never drain.
     */
    async liveTick({ commit, dispatch, state }) {
      if (state.liveBusy) return null;
      commit('setLiveBusy', true);
      try {
        const pulse = await dispatch('fetchPulse');
        if (!pulse) return null;

        // The first pulse after a day loads ADOPTS its version rather than treating it as a
        // change — otherwise every page load would immediately refetch the day it just fetched.
        if (!state.drawnVersion) {
          commit('setDrawnVersion', pulse.v);
          return pulse;
        }

        if (!shouldRefetch({ currentDate: state.date, currentVersion: state.drawnVersion, pulse })) {
          return pulse;
        }

        // Measured BEFORE the refetch, deliberately: `fetchDay` replaces `state.day`, so "did the
        // set of patients change?" has to be asked against the day currently on screen. Asking
        // afterwards compares the new count with itself and is always false.
        const patientsChanged = !!(state.day
          && Number(pulse.n) !== (state.day.appointments || []).length);

        await dispatch('fetchDay', { date: state.date, silent: true });
        commit('setDrawnVersion', pulse.v);

        // Alerts are five expensive queries and deliberately run on a slower clock than the grid:
        // a family balance does not change because somebody was marked arrived. But the set of
        // PATIENTS changing is different — a newly booked patient would otherwise sit on the
        // schedule with no icons until the ten-minute clock came round, which reads as "this
        // patient is fine" rather than "we have not looked yet".
        if (patientsChanged) dispatch('fetchAlerts', { date: state.date });

        return pulse;
      } finally {
        commit('setLiveBusy', false);
      }
    },

    /**
     * An exe signal said the day changed: tick now, then keep re-asking every
     * SIGNAL_CHASE_INTERVAL_MS until the drawn version moves (2026-09-25).
     *
     * ── WHY ────────────────────────────────────────────────────────────────────────────────────
     * The signal arrives ~1s after Open Dental changes, the cloud mirror the pulse reads ≈5–7s after
     * (the exe's 5s SQS batch → qprocessor → MySQL). One tick per signal therefore usually heard "no
     * change", and the page waited out the 30s poll. The whole decision is `nextChaseStep`
     * (utils/scheduleView/liveness.js); this only keeps the books.
     *
     * ⛔ ONE LOOP PER PAGE. A new bump while chasing RESTARTS the chase — the pending retry is
     * cancelled, it ticks now, tries go back to zero — and never starts a second loop. A tick
     * already in flight for the replaced run is disowned by `chaseRun` when it lands; whatever it
     * drew is still counted at the next evaluation, because redraws are read off `drawnVersion`.
     *
     * ⛔ A BUMP WHILE CHASING IS ONE MORE CHANGE TO WAIT FOR: `bumps + 1`, keeping `moves` and
     * `lastSeenVersion`. Re-reading a baseline here was a bug — change A's redraw then ended the
     * chase while change B was still on its way to the mirror, and B waited for the 30s poll.
     * Only a chase for the date still on screen is carried; one left over from another date would
     * stop at once on its date check and strand this bump.
     *
     * ⛔ ONLY THROUGH liveTick. Every ask goes through the single-flight tick, so date-matching and
     * the alert rule stay decided in one place. A tick dropped because another one is in flight
     * (the poll, or the tab regaining focus) is fine: the next evaluation sees whatever it drew.
     *
     * KNOWN, ACCEPTED: operatory / scheduleop signals move nothing the pulse version can see, so for
     * those the chase runs its full window and stops — at most SIGNAL_CHASE_MAX_TRIES wasted pulses —
     * and the 30s poll behaves exactly as it always has.
     */
    chaseSignal({ dispatch, state }) {
      if (chaseTimer) clearTimeout(chaseTimer);
      chaseTimer = null;
      const run = ++chaseRun;
      const carry = (chaseState && chaseState.startDate === state.date) ? chaseState : null;
      chaseState = carry
        ? {
          run,
          tries: 0,
          startDate: carry.startDate,
          bumps: carry.bumps + 1,
          moves: carry.moves,
          lastSeenVersion: carry.lastSeenVersion,
        }
        : { run, tries: 0, startDate: state.date, bumps: 1, moves: 0, lastSeenVersion: state.drawnVersion };

      const isCurrent = () => !!chaseState && chaseState.run === run;
      const decide = () => {
        Object.assign(chaseState, noteChaseRedraw(chaseState, state.drawnVersion));
        return nextChaseStep({
          tries: chaseState.tries,
          startDate: chaseState.startDate,
          currentDate: state.date,
          bumps: chaseState.bumps,
          moves: chaseState.moves,
          hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
        });
      };

      const tick = async () => {
        try {
          await dispatch('liveTick');
        } catch (err) {
          console.error('[scheduleView] signal chase tick failed:', err);
          if (isCurrent()) chaseState = null;
          return 'stop';
        }
        if (!isCurrent()) return 'superseded';

        const step = decide();
        if (step !== 'retry') {
          chaseState = null;
          return step;
        }

        chaseTimer = setTimeout(() => {
          chaseTimer = null;
          if (!isCurrent()) return;
          // Re-checked BEFORE spending a pulse: the poll, a Refresh, a date change or the tab
          // going hidden may have settled it while we waited.
          if (decide() !== 'retry') {
            chaseState = null;
            return;
          }
          chaseState.tries += 1;
          tick();
        }, SIGNAL_CHASE_INTERVAL_MS);
        return step;
      };

      return tick();
    },

    /**
     * Subscribe to the office's live signal (Phase 3Lb) — the accelerator, never a prerequisite.
     *
     * ── WHAT THIS BUYS ───────────────────────────────────────────────────────────────────────
     * The exe watches Open Dental's binlog and bumps a counter the moment a day changes, so the
     * page redraws within a few seconds — as soon as the cloud copy has the change, which the
     * signal chase waits for — instead of waiting out its 30-second poll. An office on an
     * older exe, with the setting off, or behind a firewall is SLOWER, never broken: the poll is
     * still running and is still the thing that guarantees correctness.
     *
     * ⛔ A NUDGE, NOT DATA. The document holds dates and counters — no patient, ever. Firestore
     * bypasses the PHI demo mask (it works at the axios boundary), so the schedule itself must
     * keep arriving through the API. This only decides WHEN to ask.
     *
     * Feeds the SAME `liveTick` as the timer and the focus handler, so single-flighting and
     * date-matching are decided in one place rather than reinvented per trigger — via
     * `chaseSignal`, which keeps re-asking briefly because the signal outruns the cloud mirror.
     */
    watchSignal({ commit, dispatch, state, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      // rootState.db is the Firestore instance set up at app boot. Absent in tests and in any
      // environment where Firebase failed to initialise — in which case the poll simply carries
      // on alone, which is exactly the degraded mode this feature is designed around.
      if (!siteID || !rootState.db) return null;
      if (signalUnsubscribe) return null; // already watching

      try {
        const ref = doc(rootState.db, 'schedule-view-signals', siteID);
        signalUnsubscribe = safeSnapshot(ref, (snapshot) => {
          // The listener is delivering — half of "the live signal is healthy" (liveness.js
          // isSignalHealthy), which lets the page ask /pulse every few minutes instead of every 30s.
          commit('setSignalListening', true);
          const data = snapshot.exists() ? snapshot.data() : null;

          // ⛔ ALERT counters FIRST, before the `dates` early-return below. The cloud writes
          // `alerts.<key>` for EVERY site; the exe writes `dates` only where it is installed and
          // switched on — so on most sites this document has no `dates` at all, and putting this
          // after that return would make live alert refresh silently dead there.
          const alertCounters = readAlertCounters(data, LIVE_ALERT_KEYS);
          const previousAlerts = state.alertCounters;
          commit('setAlertCounters', alertCounters);
          const bumped = changedAlertKeys(previousAlerts, alertCounters);
          if (bumped.length) dispatch('queueAlertRefresh', bumped);

          // Somebody ticked or unticked a checklist item (Phase 10c). Its own counter and its own
          // fetch — never an /alerts refetch, which a tick changes nothing in.
          const checksCounter = readAlertCounters(data, [CHECKS_SIGNAL_KEY]);
          const previousChecks = state.checksCounter;
          commit('setChecksCounter', checksCounter);
          if (changedAlertKeys(previousChecks, checksCounter).length) dispatch('queueChecksRefresh');

          const counters = readSignalCounters(data);
          if (!counters) return;

          const previous = state.signalCounters;
          commit('setSignalCounters', counters);

          // The first snapshot delivers the day's running totals; adopting it as a baseline is
          // what stops every page load refetching the day it just fetched.
          if (!previous) return;
          if (!signalChangedDate(previous, counters, state.date)) return;

          // The chase, not a bare liveTick: this signal usually arrives before the mirror the
          // pulse reads has the change, so one tick alone would hear "nothing moved".
          dispatch('chaseSignal');
        }, {
          source: 'scheduleView/signal',
          onError: (err) => {
            // The signal is not healthy any more: the page goes back to asking /pulse every 30s.
            commit('setSignalListening', false);
            // Logged, never surfaced. A signal that cannot be read is a slower page, not a broken
            // one, and an error banner over a working schedule would be strictly worse.
            console.error('[scheduleView] signal listener error:', err);
          },
        });

        commit('customer/addFirestoreListener', signalUnsubscribe, { root: true });
        return signalUnsubscribe;
      } catch (err) {
        console.error('[scheduleView] could not subscribe to the live signal:', err);
        signalUnsubscribe = null;
        commit('setSignalListening', false);
        return null;
      }
    },

    /**
     * Watch the day's patients for open Teamio tasks (Phase 4, the `task` alert).
     *
     * ⛔ CHUNKED AT 30. Firestore's `array-contains-any` rejects a 31st value outright, so a
     * 40-80 appointment day cannot be one query — it would simply fail and no icon would ever
     * appear for anybody. PracticePulse's helper caps at 10 and silently loses the 11th patient's
     * badge: fine for its handful of checked-in patients, quietly wrong for a whole day, which is
     * why that helper is not reused here.
     *
     * Every listener is registered with `customer/addFirestoreListener` so a logout tears them
     * down with everything else, and re-watching always unwatches first: the day's patients change
     * on every date change, and leaving the old listeners running would both cost reads and keep
     * yesterday's patients' icons alive.
     */
    watchTasks({ commit, dispatch, state, rootState }, patNums) {
      dispatch('unwatchTasks');

      const siteID = rootState.customer && rootState.customer.siteID;
      // No Firestore is a degraded mode, not a failure: every other alert still works and this one
      // is simply absent. Same reasoning as the live signal.
      if (!siteID || !rootState.db) return null;

      // The office's own switch. Checked here rather than at the call site so nobody can open
      // listeners for an alert the practice has turned off.
      const settings = (state.day && state.day.settings) || null;
      if (settings && settings.alerts && settings.alerts.task === false) return null;

      // ⛔ NUMBERS, not strings. `taggedPatients` holds numbers and Firestore array matching is
      // type-strict: with the day's PatNums as strings this query returned nothing on real data, so
      // the `task` icon never fired (unit tests mock Firestore, which cannot tell the difference).
      const values = taskQueryValues(patNums);
      if (!values.length) {
        // A day with no patients is watched successfully and finds nothing — `{}`, not null, so
        // the page can tell "nobody has a task" from "we are not watching".
        commit('setTaskCounts', {});
        commit('setTaskLines', {});
        return null;
      }

      const chunks = chunkPatNums(values, TASK_CHUNK_SIZE);
      taskChunkResults = chunks.map(() => ({}));

      // Injected into the pure fold (taskLines.js stays import-free). The timezone is read when a
      // snapshot lands, so a listener opened a moment before the day arrived still formats against
      // the practice's timezone. The names are a snapshot of the roster taken once per watch —
      // right when the roster is already in, empty when it is not; the `taskLinesByPatNum` getter
      // re-resolves them against the roster as it is by the time anybody reads the lines.
      const foldOpts = {
        clean: stripMentions,
        dueAtOf: data => taskDueDay(data, state.day && state.day.timezone),
        employeeNames: employeeNameMap(rootState.customer && rootState.customer.employees),
      };

      try {
        chunks.forEach((chunk, index) => {
          const q = query(
            collection(rootState.db, 'tasks'),
            where('siteID', '==', siteID),
            where('deleted', '!=', true),
            where('taggedPatients', 'array-contains-any', chunk)
          );

          const unsubscribe = safeSnapshot(q, (snapshot) => {
            const docs = [];
            snapshot.forEach((docSnap) => {
              docs.push({ id: docSnap.id, data: docSnap.data() || {} });
            });

            // ⛔ Per chunk, then combined. Each listener fires independently and knows nothing
            // about the other chunks' patients, so committing one chunk's result alone would wipe
            // every other chunk's icons (and checklist lines) each time anybody touched a task.
            // The open-task and this-chunk-only rules live in foldTaskSnapshot.
            taskChunkResults[index] = foldTaskSnapshot(docs, chunk, foldOpts);
            const combined = combineTaskChunks(taskChunkResults);
            commit('setTaskCounts', combined.counts);
            commit('setTaskLines', combined.lines);
          }, {
            source: 'scheduleView/tasks',
            onError: (err) => {
              // Logged, never surfaced — most likely a rules denial. One missing icon beats an error
              // banner over a working schedule.
              console.error('[scheduleView] task listener error:', err);
            },
          });

          taskUnsubscribes.push(unsubscribe);
          commit('customer/addFirestoreListener', unsubscribe, { root: true });
        });
        return taskUnsubscribes.length;
      } catch (err) {
        console.error('[scheduleView] could not subscribe to tasks:', err);
        dispatch('unwatchTasks');
        return null;
      }
    },

    /** Stop watching tasks. Safe to call when we never started. */
    unwatchTasks({ commit }) {
      taskUnsubscribes.forEach((unsubscribe) => {
        try {
          unsubscribe();
          commit('customer/removeFirestoreListener', unsubscribe, { root: true });
        } catch (err) {
          console.error('[scheduleView] could not unsubscribe from tasks:', err);
        }
      });
      taskUnsubscribes = [];
      taskChunkResults = [];
      // Back to null, not {}: we are no longer watching, and `{}` would claim we had looked and
      // found nobody.
      commit('setTaskCounts', null);
      commit('setTaskLines', null);
    },

    /** Stop watching. Safe to call when we never started. */
    unwatchSignal({ commit }) {
      // A pending alert refresh dies with the page: it would answer for a page nobody is on.
      if (alertRefreshTimer) clearTimeout(alertRefreshTimer);
      alertRefreshTimer = null;
      pendingAlertKeys = [];
      // Forget the alert baseline, so re-watching adopts a fresh one instead of reading every bump
      // since we left as "just changed".
      commit('setAlertCounters', null);
      if (checksRefreshTimer) clearTimeout(checksRefreshTimer);
      checksRefreshTimer = null;
      commit('setChecksCounter', null);
      // ⛔ The signal chase dies with the page too, BEFORE the early return below: otherwise a page
      // nobody is on keeps calling /pulse for the rest of the window. Nulling the state also
      // disowns a chase tick still in flight, so it schedules nothing when it lands.
      if (chaseTimer) clearTimeout(chaseTimer);
      chaseTimer = null;
      chaseState = null;
      commit('setSignalListening', false);
      if (!signalUnsubscribe) return;
      try {
        signalUnsubscribe();
        commit('customer/removeFirestoreListener', signalUnsubscribe, { root: true });
      } catch (err) {
        console.error('[scheduleView] could not unsubscribe from the live signal:', err);
      }
      signalUnsubscribe = null;
    },

    /** Restore this browser's remembered narrowing for this site. Never fails. */
    loadFilters({ commit, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      commit('setFilters', readFilters(browserStorage(), siteID));
    },

    /** Change the narrowing and remember it. */
    updateFilters({ commit, rootState }, filters) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const next = normalizeFilters(filters);
      commit('setFilters', next);
      writeFilters(browserStorage(), siteID, next);
    },

    /**
     * Restore this browser's remembered view, phone view and sort for this site (Phase 11). Never
     * fails. Reads the site's own key, so another office signed in on the same browser starts from the
     * default view; nothing remembered resets to the defaults rather than keeping the last site's.
     */
    loadTablePrefs({ commit, rootState }) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const prefs = readTablePrefs(browserStorage(), siteID);
      commit('setView', prefs.view);
      commit('setPhoneView', prefs.phoneView);
      commit('setTableSort', prefs.sort);
    },

    /**
     * Switch between the schedule and the table on an iPad / desktop, and remember it. Anything
     * unknown is the default. ⛔ The three halves of the blob are written together (read from the
     * store's state), so choosing one never resets another.
     */
    updateView({ commit, state, rootState }, view) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const next = normalizeTablePrefs({ view, phoneView: state.phoneView, sort: state.tableSort });
      commit('setView', next.view);
      writeTablePrefs(browserStorage(), siteID, next);
    },

    /** The phone's own switch — Timeline · Columns · Table — remembered next to, never over, `view`. */
    updatePhoneView({ commit, state, rootState }, phoneView) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const next = normalizeTablePrefs({ view: state.view, phoneView, sort: state.tableSort });
      commit('setPhoneView', next.phoneView);
      writeTablePrefs(browserStorage(), siteID, next);
    },

    /** Change how the table orders its rows ('time' | 'op'), and remember it. */
    updateTableSort({ commit, state, rootState }, sort) {
      const siteID = rootState.customer && rootState.customer.siteID;
      const next = normalizeTablePrefs({ view: state.view, phoneView: state.phoneView, sort });
      commit('setTableSort', next.sort);
      writeTablePrefs(browserStorage(), siteID, next);
    },

    /**
     * Save the per-office settings blob.
     *
     * ⛔ schedule-view-service NEVER WRITES. This goes through main-service's existing
     * `/upsert-settings` (via customer/upsertSettings), so there is exactly one settings writer in
     * the product and no second one to keep in sync.
     *
     * Returns `{ok, errors}` rather than throwing, so the popover can show the reason in place
     * instead of a toast that appears over a form the user can no longer correct.
     */
    async saveSettings({ commit, dispatch, rootState, getters }, { form } = {}) {
      const built = buildSettingsPayload(form, {
        siteId: rootState.customer && rootState.customer.siteID,
        // A form built from nothing holds invented values; saving it would overwrite the whole
        // practice's configuration with something nobody chose.
        settingsLoaded: !!getters.settings,
      });
      if (!built.ok) return built;

      commit('setSavingSettings', true);
      try {
        await dispatch('customer/upsertSettings', {
          settings: built.settings,
          // The site the payload was BUILT for, not a re-read — it cannot drift if somebody
          // switched office while the popover was open.
          siteID: built.siteId,
        }, { root: true });

        // Re-read the day so the page reflects what was actually STORED, not what we hoped: the
        // server's resolver is the only thing that decides what a saved blob means.
        await dispatch('fetchDay', { date: rootState.scheduleView.date, silent: true });
        return { ok: true };
      } catch (err) {
        console.error('[scheduleView] saveSettings error:', err);
        return {
          ok: false,
          errors: [extractApiError(err, 'Could not save those settings. Please try again.')],
        };
      } finally {
        commit('setSavingSettings', false);
      }
    },
  },
};
